import React from 'react';
import AppActionMenu from '../../../../components/AppActionMenu';

export default function MonthlyAttendanceTable({
  records = [],
  isLoading = false,
  onDelete,
  onResetFilters,
}) {
  if (isLoading) {
    return (
      <div className="overflow-hidden rounded-2xl border border-outline-variant/25 bg-surface-container-lowest p-12 text-center shadow-sm">
        <div className="flex flex-col items-center justify-center gap-3">
          <div className="h-8 w-8 animate-spin rounded-full border-3 border-primary border-t-transparent" />
          <p className="text-xs font-semibold text-on-surface-variant">Loading Monthly Attendance...</p>
        </div>
      </div>
    );
  }

  if (records.length === 0) {
    return (
      <div className="overflow-hidden rounded-2xl border border-outline-variant/25 bg-surface-container-lowest p-12 text-center shadow-sm">
        <div className="flex flex-col items-center justify-center gap-3">
          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-primary/10 text-primary">
            <span className="material-symbols-outlined text-3xl">event_repeat</span>
          </div>
          <h3 className="text-lg font-bold text-on-surface">No Monthly Attendance Records</h3>
          <p className="text-xs text-on-surface-variant max-w-sm">
            No processed attendance found for the selected filters. Run Monthly Process to generate summaries.
          </p>
          {onResetFilters && (
            <button
              type="button"
              onClick={onResetFilters}
              className="mt-2 rounded-xl bg-primary px-5 py-2 text-xs font-bold text-on-primary shadow-sm hover:bg-primary/90 transition cursor-pointer"
            >
              Reset Filters
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-2xl border border-outline-variant/25 bg-surface-container-lowest shadow-sm">
      <div className="overflow-x-auto">
        <table className="w-full min-w-[960px] text-left text-sm">
          <thead>
            <tr className="border-b border-outline-variant/20 bg-surface-container-low/60 text-xs font-bold uppercase tracking-[0.08em] text-on-surface-variant">
              <th className="px-5 py-3.5">Emp Code</th>
              <th className="px-5 py-3.5">Employee Name</th>
              <th className="px-5 py-3.5">Company</th>
              <th className="px-5 py-3.5">Branch</th>
              <th className="px-5 py-3.5">Department</th>
              <th className="px-5 py-3.5">Month — Year</th>
              <th className="px-5 py-3.5 text-center">Total Days</th>
              <th className="px-5 py-3.5 text-right text-emerald-600 dark:text-emerald-400">Present</th>
              <th className="px-5 py-3.5 text-right text-rose-600 dark:text-rose-400">Absent</th>
              <th className="px-5 py-3.5 text-center">Status</th>
              <th className="w-20 px-5 py-3.5 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-outline-variant/15">
            {records.map((item) => (
              <tr
                key={`${item.id}-${item.empId}-${item.month}-${item.year}`}
                className="transition-colors hover:bg-surface-container-low/50 group"
              >
                {/* Emp Code */}
                <td className="px-5 py-3.5 text-xs font-mono font-bold text-on-surface-variant">
                  {item.empCode || '-'}
                </td>

                {/* Employee Name */}
                <td className="px-5 py-3.5 font-bold text-on-surface group-hover:text-primary transition-colors">
                  {item.empName}
                </td>

                {/* Company */}
                <td className="px-5 py-3.5 text-xs text-on-surface-variant">
                  {item.companyName}
                </td>

                {/* Branch */}
                <td className="px-5 py-3.5 text-xs text-on-surface-variant">
                  {item.branchName}
                </td>

                {/* Department */}
                <td className="px-5 py-3.5 text-xs text-on-surface-variant">
                  {item.departmentName}
                </td>

                {/* Month — Year */}
                <td className="px-5 py-3.5 text-xs font-semibold text-primary whitespace-nowrap">
                  {item.monthName || item.month} — {item.year}
                </td>

                {/* Total Days */}
                <td className="px-5 py-3.5 text-center text-xs font-mono font-semibold text-on-surface">
                  {item.totalDays || '-'}
                </td>

                {/* Present Days */}
                <td className="px-5 py-3.5 text-right font-bold text-emerald-600 dark:text-emerald-400">
                  {Number(item.presentDays || 0).toFixed(2)}
                </td>

                {/* Absent Days */}
                <td className="px-5 py-3.5 text-right font-bold text-rose-600 dark:text-rose-400">
                  {Number(item.absentDays || 0).toFixed(2)}
                </td>

                {/* Status Badge */}
                <td className="px-5 py-3.5 text-center">
                  <span
                    className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-bold ${
                      item.status === 'Processed'
                        ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400'
                        : 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400'
                    }`}
                  >
                    {item.status}
                  </span>
                </td>

                {/* Actions */}
                <td
                  className="px-5 py-3.5 text-right"
                  onClick={(e) => e.stopPropagation()}
                >
                  <AppActionMenu
                    triggerLabel=""
                    triggerIcon="more_vert"
                    showChevron={false}
                    triggerClassName="rounded-lg p-1.5 text-outline-variant transition-colors hover:bg-surface-container-low hover:text-primary bg-transparent cursor-pointer"
                    items={[
                      { key: 'delete', label: 'Delete Record', icon: 'delete', danger: true },
                    ]}
                    onSelect={(key) => {
                      if (key === 'delete') {
                        onDelete?.(item.id, item.empName);
                      }
                    }}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
